/**
 * The tagged cell shapes the Rust bridge can send, and the predicates that
 * recognise them.
 *
 * Ordinary cells arrive as JSON scalars, arrays or objects. Two kinds cannot:
 *
 *  - binary, which used to be `String::from_utf8`'d — turning valid-UTF-8 blobs
 *    into indistinguishable text and everything else into the literal
 *    `<binary:N bytes>`, discarding the bytes;
 *  - values the driver could not decode at all, which used to collapse into
 *    JSON null and so were indistinguishable from a real NULL.
 *
 * Both now arrive tagged. What they have in common is that the grid renders a
 * SUMMARY of them (a size, a type name) rather than the value, which is why
 * neither may be edited or used as a `=` operand — hence `isOpaqueCell`.
 *
 * These live in their own module rather than beside the SQL builders because
 * both the builders (dbGridSql) and the serializers (dbExportUtils) need them,
 * and importing one from the other would make the two mutually dependent.
 */

/** A value the driver refused to decode, tagged with the column type. */
export function isUndecoded(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v) && typeof v.__undecoded === 'string';
}

/** A binary cell, carried as base64 rather than mangled into text. */
export function isBytesTag(v) {
  return (
    !!v && typeof v === 'object' && !Array.isArray(v) && v.__type === 'bytes' && typeof v.b64 === 'string'
  );
}

/**
 * Cells that must not be edited in the grid or compared with `=`: what is on
 * screen for them is a summary, not the value.
 */
export function isOpaqueCell(v) {
  return isUndecoded(v) || isBytesTag(v);
}
